import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Bucket from "@/services/bucket/bucket";
import type { GetBucket } from "@/dtos/Bucket.dto";

export interface BucketStatsParams {
  organizationId: string;
  bucket?: string;
  offset?: number;
  createdById?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useBucketStats(params: Ref<BucketStatsParams | null> | BucketStatsParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["bucketStats", "disabled"];
    return ["bucketStats", p.organizationId, p.bucket || "", p.offset || 0, p.createdById || ""];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetBucket = {
        organizationId: p.organizationId,
      };

      if (p.bucket) queryParams.bucket = p.bucket;
      if (p.offset !== undefined) queryParams.offset = p.offset;
      if (p.createdById) queryParams.createdById = p.createdById;

      const result = await Bucket.getBucketStats(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load bucket stats");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
